/**
 * Flagged Posts View
 */
import React, { useState } from 'react';
import { TextKeys } from '@lib/i18n/configureI18n';
import { useTranslation } from 'react-i18next';
import { Card } from '@mui/material';
import WithSideBar from '@shared/templates/with-side-bar/WithSideBar';
import Tabs from '@shared/components/tabs/Tabs';
import Timeline from '@shared/components/timeline/Timeline';
import { fetchFlaggedPosts } from '@data/timeline/timeline.thunk';
import TimelineSelectors from '@data/timeline/timeline.selector';
import SubmittingDialog from '@shared/components/dialogs/submittion-dialog/SubmittingDialog';
import { TimeLines } from '@data/timeline/timeline.model';
import { AsyncThunk } from '@reduxjs/toolkit';
import { FlagReviewButton } from '@view/flagged-posts/FlagReviewButton';
import { ReviewPostProvider } from './hooks/useReviewPosts';

const FlaggedPosts: React.FC<unknown> = () => {
  const { t } = useTranslation();
  const tabs = ['flagged posts', 'reviewed posts'];

  const [submitting, setSubmitting] = useState(false);

  const [selectedTab, setSelectedTab] = React.useState(0);

  const renderFlaggedPosts = React.useCallback(() => {
    return (
      <Timeline
        timelineFetcher={
          fetchFlaggedPosts as unknown as AsyncThunk<
            never,
            { page: number },
            never
          >
        }
        selector={TimelineSelectors.flagged.selectNotReviewedPostsIds}
        hasMoreSelector={TimelineSelectors.flagged.hasMoreSelector}
        pageSelector={TimelineSelectors.flagged.pageSelector}
        emptyListMessage={t(TextKeys.NoFlaggedPosts)}
        timeline={TimeLines.FLAGGED}
      />
    );
  }, [t]);

  const renderReviewedPostsList = React.useCallback(() => {
    return (
      <Timeline
        timelineFetcher={
          fetchFlaggedPosts as unknown as AsyncThunk<
            never,
            { page: number },
            never
          >
        }
        selector={TimelineSelectors.flagged.selectReviewedPostsIds}
        hasMoreSelector={TimelineSelectors.flagged.hasMoreSelector}
        pageSelector={TimelineSelectors.flagged.pageSelector}
        timeline={TimeLines.REVIEWED_FLAGGED}
      />
    );
  }, []);

  return (
    <ReviewPostProvider>
      <WithSideBar>
        <div className="flex justify-center w-full h-full pb-4">
          <div className="relative w-full h-auto mt-5 flex-col-center max-w-[540px]">
            <span className="w-full mb-4 flex-col-center h3 text-on-primary">
              {t(TextKeys.ReviewFlaggedPosts)}
            </span>
            <SubmittingDialog
              show={submitting}
              message={t(TextKeys.SubmittingReview)}
            />
            <Tabs tabs={tabs} onTabChanged={setSelectedTab} />
            <Card className="w-full h-full max-w-[540px] mt-[30px]">
              {selectedTab === 0
                ? renderFlaggedPosts()
                : renderReviewedPostsList()}
            </Card>
            <FlagReviewButton
              setSubmitting={setSubmitting}
              show={selectedTab === 0}
            />
          </div>
        </div>
      </WithSideBar>
    </ReviewPostProvider>
  );
};

export default FlaggedPosts;
